/* eslint-disable camelcase */

import axios from '@/plugins/axios.js';
import { DialogService } from '@/services/DialogService.js';
import toastService from '@/services/ToastService.js';
import { parseQuizReviewQuestions } from '@/utilities/gradebook/parseQuizReviewQuestions.js';

export default class QuizAttemptHelper {
    constructor({ store = null, router = null, payload = null, attemptId = null }) {
        this.store = store;
        this.router = router;
        this.user_id = store.rootGetters['User/get']('id');
        this.quiz_active_id = attemptId || store.rootGetters['User/get']('quiz_active_id');
        this.payload = payload;
        this.attemptId = attemptId;
    }

    async authorizeQuizAttempt({ quizType = null }) {
        const type = this.userHasActiveQuiz() || this.attemptId ? 'get' : 'post';

        const data = await this.launchQuiz({ method: type, quizType });

        if (!this.hasCriticalUserData()) {
            const isLockdown =
                this.payload.lockdown ||
                data?.quiz_activity?.lockdown ||
                data?.survey_activity?.lockdown ||
                data?.automated_survey_assignment?.is_locked_now;

            if (isLockdown) {
                this.store.commit('User/SET', { field: 'quiz_active_id', value: data.id }, { root: true });
            }

            if (this.payload.assignment) {
                this.store.commit(
                    'User/SET',
                    {
                        field: 'quiz_activity',
                        value: this.payload.assignment,
                    },
                    { root: true }
                );
            } else {
                this.store.commit(
                    'User/SET',
                    {
                        field: 'quiz_activity',
                        value: data.activity || data.quiz_activity?.activity || data.survey_activity?.activity,
                    },
                    { root: true }
                );
            }

            this.store.commit('User/SET', { field: 'quiz_id', value: data.quiz_version_id }, { root: true });
        }

        return data;
    }

    handleError(error) {
        const errorMessage = error?.response?.data?.message;

        return DialogService.alert({
            title: 'Error',
            message: `
                     <p>Looks like something went wrong</p>
                     </br>
                     <p>${!errorMessage ? error : `Error: ${errorMessage}`}</p>
                    `,
            type: 'is-danger',
            hasIcon: true,
            icon: 'exclamation-circle',
            iconPack: 'unicons',
            ariaRole: 'alertdialog',
            ariaModal: true,
            onConfirm: () => {
                if (error?.response?.data?.code === 400) return;

                // Hard redirect to activity page
                toastService.open('Reloading page');

                const { courseId, activityId } = this.payload;

                window.location.href = `/courses/${courseId}/activities/${activityId}`;
            },
        });
    }

    async launchQuiz({ method, quizType = null }) {
        try {
            let attemptKey = null;
            let api = `${this.store.getters.api('quiz_attempt')}${method === 'get' ? `/${this.quiz_active_id}` : ''}`;

            if (quizType === 'survey') {
                attemptKey = 'survey_activity_id';
            } else if (quizType !== 'automatedSurvey') {
                attemptKey = 'quiz_activity_id';
            }

            if (method === 'get') {
                if (quizType === 'survey') {
                    api += '?include=surveyActivity.activity';
                } else {
                    api += '?include=quizActivity.activity';
                }
            }

            const payload = {
                quiz_version_id: this.payload.quizVersionId,
                user_id: this.user_id,
                password: this.payload.password,
                is_preview: this.payload.preview,
            };

            if (quizType !== 'automatedSurvey') {
                payload[attemptKey] = this.userHasActiveQuiz() ? this.quiz_active_id : this.payload.quizActivityId;
            }

            return await axios[method](`${api}`, payload, { errorHandle: false }).then(resp => resp.data.data);
        } catch (e) {
            this.handleError(e);

            return e;
        }
    }

    navigateToQuiz(type, quizId, attemptId) {
        // Preview must hit quiz-attempt-preview. The quiz-attempt guard requires
        // "create a quiz attempt", which group-only instructors do not have.
        const name = type === 'quiz' && this.payload?.preview ? 'quiz-attempt-preview' : `${type}-attempt`;

        this.router.push({
            name,
            params: {
                ...this.payload,
                attemptId,
                id: quizId,
            },
        });
    }

    // eslint-disable-next-line class-methods-use-this
    parseSelectedAnswers(item) {
        return parseQuizReviewQuestions(item);
    }

    userHasActiveQuiz() {
        return this.store.rootGetters['User/hasActiveQuiz'];
    }

    hasCriticalUserData() {
        const required = ['quiz_active_id', 'quiz_activity', 'quiz_id'];

        return required.every(check => this.store.rootGetters['User/get'](check));
    }
}
